import { ChangeEvent, Fragment, FormEvent, useEffect, useMemo, useState } from 'react';
import { Badge } from './components/Badge';
import { Logo } from './components/Logo';
import { MetricCard } from './components/MetricCard';
import { WebsiteWorkspace } from './components/WebsiteWorkspace';
import { api, setStoredAccessToken, SiktraApiError } from './shared/api';
import { formatDate, formatDateTime, stringifyMetadata, daysUntil } from './shared/format';
import { buildDashboard, buildFamilies, buildUsers } from './shared/normalizers';
import type { AdminUserRow, AuditEntry, AuthState, EnterpriseLogEntry, EnterpriseLogPagination, EnterpriseLogQuery, FamilyMemberRole, FamilyMemberSummary, FamilySummary, HealthStatus, LoggingSettings, UserSummary } from './shared/types';

type View = 'dashboard' | 'users' | 'families' | 'licenses' | 'roles' | 'website' | 'logging' | 'audit' | 'system';
type UserDialogState = { mode: 'create'; user?: null } | { mode: 'edit'; user: UserSummary } | null;

const ownerEmail = import.meta.env.VITE_SIKTRA_OWNER_EMAIL || 'hahner.yannick@icloud.com';
const DEFAULT_LOG_FILTER_LIMIT = 50;
const DEFAULT_LOG_PAGINATION: EnterpriseLogPagination = {
  limit: DEFAULT_LOG_FILTER_LIMIT,
  offset: 0,
  total: 0,
  hasNext: false,
  hasPrevious: false,
  nextOffset: null,
  previousOffset: null,
};

function daysAgoIso(days: number): string {
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
}

function defaultLogFilters(): EnterpriseLogQuery {
  return { limit: DEFAULT_LOG_FILTER_LIMIT, offset: 0, since: daysAgoIso(7) };
}

function isoToLocalDateTimeValue(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);
  return localDate.toISOString().slice(0, 16);
}

function localDateTimeValueToIso(value: string): string | undefined {
  if (!value.trim()) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

const views: Array<{ id: View; label: string; icon: string }> = [
  { id: 'dashboard', label: 'Dashboard', icon: '✦' },
  { id: 'users', label: 'User', icon: '👤' },
  { id: 'families', label: 'Familien', icon: '☾' },
  { id: 'licenses', label: 'Lizenzen', icon: '◆' },
  { id: 'roles', label: 'Rollen', icon: '◈' },
  { id: 'website', label: 'Website', icon: '✧' },
  { id: 'logging', label: 'Logging', icon: '◌' },
  { id: 'audit', label: 'Audit', icon: '≋' },
  { id: 'system', label: 'System', icon: '⚙' },
];

function errorMessage(error: unknown): string {
  if (error instanceof SiktraApiError) return `${error.message} (${error.status}/${error.code})`;
  if (error instanceof Error) return error.message;
  return 'Unbekannter Fehler';
}

export function App() {
  const [auth, setAuth] = useState<AuthState>({ authenticated: false, user: null });
  const [booting, setBooting] = useState(true);
  const [view, setView] = useState<View>('dashboard');
  const [rows, setRows] = useState<AdminUserRow[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loggingSettings, setLoggingSettings] = useState<LoggingSettings | null>(null);
  const [loggingApiMissing, setLoggingApiMissing] = useState(false);
  const [enterpriseLogs, setEnterpriseLogs] = useState<EnterpriseLogEntry[]>([]);
  const [logsApiMissing, setLogsApiMissing] = useState(false);
  const [logFilters, setLogFilters] = useState<EnterpriseLogQuery>(() => defaultLogFilters());
  const [logPagination, setLogPagination] = useState<EnterpriseLogPagination>(DEFAULT_LOG_PAGINATION);
  const [userDialog, setUserDialog] = useState<UserDialogState>(null);

  const users = useMemo(() => buildUsers(rows), [rows]);
  const families = useMemo(() => buildFamilies(rows), [rows]);
  const dashboard = useMemo(() => buildDashboard(rows), [rows]);

  async function refreshData(query = search, filters = logFilters) {
    if (!auth.user?.isAdmin) return;
    setLoading(true);
    setError(null);
    try {
      const [nextRows, nextAudit, nextHealth, nextLogs] = await Promise.all([
        api.adminUsers(query),
        api.auditLog().catch(() => []),
        api.health().catch(() => null),
        api.enterpriseLogs(filters).catch(() => null),
      ]);
      setRows(nextRows);
      setAudit(nextAudit);
      setHealth(nextHealth);
      setEnterpriseLogs(nextLogs?.entries ?? []);
      setLogPagination(nextLogs?.pagination ?? DEFAULT_LOG_PAGINATION);
      setLogsApiMissing(nextLogs === null);
      await loadLoggingSettings();
    } catch (currentError) {
      setError(errorMessage(currentError));
    } finally {
      setLoading(false);
    }
  }

  async function loadLoggingSettings() {
    try {
      const settings = await api.platformLoggingSettings();
      setLoggingSettings(settings);
      setLoggingApiMissing(false);
    } catch {
      setLoggingSettings({ mode: 'standard', debugUntil: null });
      setLoggingApiMissing(true);
    }
  }

  useEffect(() => {
    let mounted = true;
    api.me()
      .then((state) => {
        if (!mounted) return;
        setAuth(state);
      })
      .catch(() => {
        if (!mounted) return;
        setStoredAccessToken(null);
        setAuth({ authenticated: false, user: null });
      })
      .finally(() => {
        if (mounted) setBooting(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (auth.user?.isAdmin) refreshData('', logFilters);
  }, [auth.user?.id, auth.user?.isAdmin]);

  async function logout() {
    await api.logout();
    setAuth({ authenticated: false, user: null });
    setRows([]);
    setAudit([]);
    setEnterpriseLogs([]);
    setLogPagination(DEFAULT_LOG_PAGINATION);
    setView('dashboard');
  }

  async function downloadEnterpriseLogs() {
    try {
      const csv = await api.enterpriseLogsCsv(logFilters);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `siktra-enterprise-logs-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
      setNotice('Enterprise Logs wurden als CSV vorbereitet.');
    } catch (currentError) {
      setError(errorMessage(currentError));
    }
  }

  async function refreshLogs(filters: EnterpriseLogQuery) {
    setLogFilters(filters);
    await refreshData(search, filters);
  }

  async function archiveEnterpriseLogs(logIds: string[]) {
    const uniqueLogIds = [...new Set(logIds)].filter(Boolean);
    if (uniqueLogIds.length === 0) return;
    const result = await api.archiveEnterpriseLogs(uniqueLogIds, 'In Siktra Manage aus der Übersicht archiviert');
    const archivedCount = Number(result.archivedCount ?? uniqueLogIds.length);
    setNotice(`${archivedCount} Log-Eintrag${archivedCount === 1 ? '' : 'e'} archiviert.`);
    const currentOffset = Number(logFilters.offset ?? 0);
    const currentLimit = Number(logFilters.limit ?? DEFAULT_LOG_FILTER_LIMIT);
    const nextOffset = enterpriseLogs.length <= uniqueLogIds.length && currentOffset > 0 ? Math.max(currentOffset - currentLimit, 0) : currentOffset;
    await refreshLogs({ ...logFilters, offset: nextOffset });
  }

  async function archiveAllEnterpriseLogs(filters: EnterpriseLogQuery) {
    const result = await api.archiveEnterpriseLogsByFilter(filters, 'Alle gefilterten Logs in Siktra Manage aus der Übersicht entfernt');
    const archivedCount = Number(result.archivedCount ?? 0);
    setNotice(`${archivedCount} Log-Eintrag${archivedCount === 1 ? '' : 'e'} aus der Übersicht entfernt.`);
    await refreshLogs({ ...filters, offset: 0 });
  }

  async function handleUserChanged(message: string) {
    setNotice(message);
    setUserDialog(null);
    await refreshData(search, logFilters);
  }

  if (booting) {
    return <Shell><div className="centerPanel"><Logo /><p>Management-Kontext wird geladen…</p></div></Shell>;
  }

  if (!auth.authenticated || !auth.user) {
    return <Shell><LoginScreen onLogin={setAuth} /></Shell>;
  }

  if (!auth.user.isAdmin) {
    return (
      <Shell>
        <div className="centerPanel blockedPanel">
          <Logo />
          <h1>Kein Platform-Zugriff</h1>
          <p>Dieses Tool ist nur für den Siktra Sleep Platform Owner freigegeben.</p>
          <p className="muted">Erwarteter Owner: {ownerEmail}</p>
          <button className="button button--secondary" onClick={logout}>Abmelden</button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <aside className="sidebar">
        <Logo />
        <nav>
          {views.map((item) => (
            <button key={item.id} className={view === item.id ? 'navItem navItem--active' : 'navItem'} onClick={() => setView(item.id)}>
              <span>{item.icon}</span>{item.label}
            </button>
          ))}
        </nav>
        <div className="sidebarFooter">
          <span>API</span>
          <strong>{api.baseUrl}</strong>
          <button className="button button--ghost" onClick={logout}>Abmelden</button>
        </div>
      </aside>

      <main className="main">
        <header className="topbar">
          <div>
            <span className="eyebrow">Siktra Sleep Enterprise</span>
            <h1>{views.find((item) => item.id === view)?.label}</h1>
          </div>
          <div className="userChip">
            <span>{auth.user.displayName || auth.user.email}</span>
            <strong>{auth.user.email}</strong>
          </div>
        </header>

        {error ? <div className="alert alert--bad">{error}</div> : null}
        {notice ? <div className="alert alert--good">{notice}</div> : null}
        {loading ? <div className="alert">Daten werden aktualisiert…</div> : null}

        {view === 'dashboard' ? <DashboardView stats={dashboard} health={health} audit={audit} onRefresh={() => refreshData()} /> : null}
        {view === 'users' ? <UsersView users={users} search={search} setSearch={setSearch} onSearch={() => refreshData(search)} onCreate={() => setUserDialog({ mode: 'create' })} onEdit={(user) => setUserDialog({ mode: 'edit', user })} /> : null}
        {view === 'families' ? <FamiliesView families={families} /> : null}
        {view === 'licenses' ? <LicensesView families={families} onUpdated={async () => { setNotice('Lizenz wurde aktualisiert.'); await refreshData(); }} onError={(message) => setError(message)} /> : null}
        {view === 'roles' ? <RolesView families={families} onUpdated={async () => { setNotice('Rolle wurde aktualisiert.'); await refreshData(); }} onError={(message) => setError(message)} /> : null}
        {view === 'website' ? <WebsiteWorkspace onNotice={(message) => setNotice(message)} onError={(message) => setError(message)} /> : null}
        {view === 'logging' ? <LoggingView settings={loggingSettings} logs={enterpriseLogs} pagination={logPagination} settingsApiMissing={loggingApiMissing} logsApiMissing={logsApiMissing} filters={logFilters} onSaved={async () => { setNotice('Logging-Einstellung wurde gespeichert.'); await loadLoggingSettings(); }} onRefreshLogs={refreshLogs} onArchiveLogs={archiveEnterpriseLogs} onArchiveAllLogs={archiveAllEnterpriseLogs} onDownload={downloadEnterpriseLogs} onError={(message) => setError(message)} /> : null}
        {view === 'audit' ? <AuditView entries={audit} /> : null}
        {view === 'system' ? <SystemView health={health} rows={rows} loggingApiMissing={loggingApiMissing} onRefresh={() => refreshData()} /> : null}
      </main>

      {userDialog ? (
        <UserEditorDialog
          dialog={userDialog}
          onClose={() => setUserDialog(null)}
          onSaved={handleUserChanged}
          onError={(message) => setError(message)}
        />
      ) : null}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return <div className="appShell">{children}</div>;
}

function LoginScreen({ onLogin }: { onLogin: (state: AuthState) => void }) {
  const [email, setEmail] = useState(ownerEmail);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const state = await api.login(email, password);
      onLogin(state);
    } catch (currentError) {
      setError(errorMessage(currentError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="loginLayout">
      <section className="loginHero">
        <Logo />
        <h1>Enterprise Control Center für Siktra Sleep.</h1>
        <p>Verwalte User, Familien, Lizenzen, Logging und Audit-Daten über ein eigenes gesichertes Webtool.</p>
        <div className="heroChecks">
          <span>Owner Guard</span>
          <span>Lizenzverwaltung</span>
          <span>Audit-ready</span>
        </div>
      </section>
      <form className="loginCard" onSubmit={submit}>
        <span className="eyebrow">manage.siktra-sleep.com</span>
        <h2>Platform Login</h2>
        <label>
          E-Mail
          <input value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" />
        </label>
        <label>
          Passwort
          <input value={password} onChange={(event) => setPassword(event.target.value)} type="password" autoComplete="current-password" />
        </label>
        {error ? <div className="alert alert--bad">{error}</div> : null}
        <button className="button" disabled={busy}>{busy ? 'Login läuft…' : 'Einloggen'}</button>
        <p className="muted">Nach dem Login wird zusätzlich `user.isAdmin` aus der API geprüft.</p>
      </form>
    </div>
  );
}

function DashboardView({ stats, health, audit, onRefresh }: { stats: ReturnType<typeof buildDashboard>; health: HealthStatus | null; audit: AuditEntry[]; onRefresh: () => void }) {
  return (
    <section className="stack">
      <div className="toolbar"><button className="button button--secondary" onClick={onRefresh}>Aktualisieren</button></div>
      <div className="metricGrid">
        <MetricCard label="User" value={stats.usersTotal} hint="registriert" />
        <MetricCard label="Familien" value={stats.familiesTotal} hint="Cloud-Familien" />
        <MetricCard label="Kinder" value={stats.childrenTotal} hint="aktive Profile" />
        <MetricCard label="Mitglieder" value={stats.membersTotal} hint="Familienrollen" />
        <MetricCard label="Aktive Lizenzen" value={stats.licensesActive} hint="unlocked" />
        <MetricCard label="Trials" value={stats.licensesTrial} hint="laufend" />
        <MetricCard label="Abgelaufen" value={stats.licensesExpired} hint="expired" />
        <MetricCard label="Gesperrt" value={stats.licensesDisabled} hint="disabled" />
      </div>
      <div className="twoColumns">
        <article className="panel">
          <h2>Operative Warnungen</h2>
          {stats.openWarnings.length === 0 ? <p className="muted">Keine Warnungen aus den geladenen Daten.</p> : stats.openWarnings.map((warning) => <div className="alert alert--warn" key={warning}>{warning}</div>)}
        </article>
        <article className="panel">
          <h2>Systemstatus</h2>
          <dl className="detailList">
            <dt>API</dt><dd>{health?.ok ? 'online' : 'unbekannt'}</dd>
            <dt>Datenbank</dt><dd>{String(health?.database ?? '—')}</dd>
            <dt>Letzter Health Check</dt><dd>{formatDateTime(health?.timestamp as string | undefined)}</dd>
            <dt>Audit Einträge geladen</dt><dd>{audit.length}</dd>
          </dl>
        </article>
      </div>
    </section>
  );
}

function UsersView({ users, search, setSearch, onSearch, onCreate, onEdit }: { users: UserSummary[]; search: string; setSearch: (value: string) => void; onSearch: () => void; onCreate: () => void; onEdit: (user: UserSummary) => void }) {
  return (
    <section className="panel">
      <div className="toolbar">
        <input placeholder="User, Familie oder Lizenz suchen…" value={search} onChange={(event) => setSearch(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') onSearch(); }} />
        <button className="button button--secondary" onClick={onSearch}>Suchen</button>
        <button className="button" onClick={onCreate}>Account anlegen</button>
      </div>
      <div className="tableWrap">
        <table className="denseTable">
          <thead><tr><th>User</th><th>Status</th><th>Familien</th><th>Lizenzen</th><th>Kontext</th></tr></thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} className="clickableRow" onClick={() => onEdit(user)}>
                <td><strong>{user.displayName}</strong><span>{user.email}</span></td>
                <td>{user.disabledAt ? <span className="statusPill statusPill--bad">Gesperrt</span> : <span className="statusPill statusPill--good">Aktiv</span>}</td>
                <td>{user.families.length || '—'}</td>
                <td><div className="badgeRow">{user.families.map((family) => <Badge key={family.id} value={family.licenseStatus} />)}</div></td>
                <td>{user.families.map((family) => family.name).join(', ') || 'Keine Familie'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function UserEditorDialog({ dialog, onClose, onSaved, onError }: { dialog: NonNullable<UserDialogState>; onClose: () => void; onSaved: (message: string) => Promise<void>; onError: (message: string) => void }) {
  const editing = dialog.mode === 'edit';
  const user = editing ? dialog.user : null;
  const [email, setEmail] = useState(user?.email ?? '');
  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [disabled, setDisabled] = useState(Boolean(user?.disabledAt));
  const [password, setPassword] = useState('');
  const [createFamily, setCreateFamily] = useState(true);
  const [familyName, setFamilyName] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setEmail(user?.email ?? '');
    setDisplayName(user?.displayName ?? '');
    setDisabled(Boolean(user?.disabledAt));
    setGeneratedPassword(null);
  }, [user?.id]);

  async function save() {
    setBusy(true);
    try {
      if (editing && user) {
        await api.updateAdminUser(user.id, { email, displayName, disabled });
        await onSaved('Account wurde aktualisiert.');
      } else {
        const result = await api.createAdminUser({ email, displayName, password: password || undefined, createFamily, familyName: familyName || undefined });
        if (result.generatedPassword) setGeneratedPassword(result.generatedPassword);
        await onSaved(result.generatedPassword ? 'Account wurde angelegt. Passwort wurde generiert.' : 'Account wurde angelegt.');
      }
    } catch (currentError) {
      onError(errorMessage(currentError));
    } finally {
      setBusy(false);
    }
  }

  async function resetPassword() {
    if (!editing || !user) return;
    setBusy(true);
    try {
      const result = await api.resetAdminUserPassword(user.id);
      setGeneratedPassword(result.generatedPassword ?? result.password ?? null);
    } catch (currentError) {
      onError(errorMessage(currentError));
    } finally {
      setBusy(false);
    }
  }

  async function deleteUser() {
    if (!editing || !user) return;
    if (!window.confirm(`Account ${user.email} wirklich löschen?`)) return;
    setBusy(true);
    try {
      await api.deleteAdminUser(user.id);
      await onSaved('Account wurde gelöscht.');
    } catch (currentError) {
      onError(errorMessage(currentError));
    } finally {
      setBusy(false);
    }
  }

  async function copyPassword() {
    if (!generatedPassword) return;
    await navigator.clipboard.writeText(generatedPassword);
  }

  return (
    <div className="modalBackdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="modalCard" role="dialog" aria-modal="true">
        <div className="panelHeader">
          <div>
            <span className="eyebrow">{editing ? 'Full Edit Mode' : 'Neuer Account'}</span>
            <h2>{editing ? user?.email : 'Account anlegen'}</h2>
          </div>
          <button className="button button--ghost" onClick={onClose}>Schließen</button>
        </div>
        <div className="formGrid">
          <label>E-Mail<input value={email} onChange={(event) => setEmail(event.target.value)} /></label>
          <label>Name<input value={displayName} onChange={(event) => setDisplayName(event.target.value)} /></label>
          {editing ? <label className="checkboxLine"><input type="checkbox" checked={disabled} onChange={(event) => setDisabled(event.target.checked)} /> Account sperren</label> : null}
          {!editing ? <label>Passwort optional<input value={password} onChange={(event) => setPassword(event.target.value)} placeholder="leer lassen = Generator" /></label> : null}
          {!editing ? <label className="checkboxLine"><input type="checkbox" checked={createFamily} onChange={(event) => setCreateFamily(event.target.checked)} /> Familie direkt anlegen</label> : null}
          {!editing && createFamily ? <label>Familienname<input value={familyName} onChange={(event) => setFamilyName(event.target.value)} placeholder="optional" /></label> : null}
        </div>
        {generatedPassword ? (
          <div className="generatedPasswordBox">
            <strong>Generiertes Passwort</strong>
            <input readOnly value={generatedPassword} onFocus={(event) => event.currentTarget.select()} />
            <button className="button button--secondary" onClick={copyPassword}>Kopieren</button>
          </div>
        ) : null}
        <div className="modalActions">
          {editing ? <button className="button button--secondary" disabled={busy} onClick={resetPassword}>Password Reset</button> : null}
          {editing ? <button className="button button--danger" disabled={busy} onClick={deleteUser}>Löschen</button> : null}
          <button className="button" disabled={busy || !email.trim()} onClick={save}>{busy ? 'Speichern…' : 'Speichern'}</button>
        </div>
      </section>
    </div>
  );
}

function FamiliesView({ families }: { families: FamilySummary[] }) {
  return (
    <section className="panel">
      <div className="tableWrap">
        <table className="denseTable">
          <thead><tr><th>Familie</th><th>Owner</th><th>Mitglieder</th><th>Rollen</th><th>Kinder</th><th>Lizenz</th><th>Ablauf</th></tr></thead>
          <tbody>
            {families.map((family) => (
              <tr key={family.id}>
                <td><strong>{family.name}</strong><span>{family.id}</span></td>
                <td>{family.ownerEmail || '—'}</td>
                <td>{family.memberCount}</td>
                <td>{family.members?.map((member) => <span className="rolePill" key={member.id}>{member.roleLabel ?? member.role}</span>) ?? '—'}</td>
                <td>{family.childCount}</td>
                <td><Badge value={family.licenseStatus} /></td>
                <td>{formatDate(family.trialEndsAt ?? family.licenseExpiresAt)} <span className="muted">{daysUntil(family.trialEndsAt ?? family.licenseExpiresAt)}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function RolesView({ families, onUpdated, onError }: { families: FamilySummary[]; onUpdated: () => Promise<void>; onError: (message: string) => void }) {
  const [selected, setSelected] = useState('');
  const [members, setMembers] = useState<FamilyMemberSummary[]>([]);
  const [memberId, setMemberId] = useState('');
  const [role, setRole] = useState<FamilyMemberRole>('caregiver');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const family = families.find((item) => item.id === selected) ?? families[0];

  useEffect(() => {
    if (!selected && families[0]) setSelected(families[0].id);
  }, [families, selected]);

  useEffect(() => {
    let cancelled = false;
    if (!family) {
      setMembers([]);
      return;
    }
    api.adminFamilyMembers(family.id)
      .then((nextMembers) => {
        if (cancelled) return;
        setMembers(nextMembers);
        const firstEditable = nextMembers.find((member) => member.role !== 'owner');
        setMemberId((current) => current || firstEditable?.id || '');
      })
      .catch((currentError) => {
        if (!cancelled) onError(errorMessage(currentError));
      });
    return () => { cancelled = true; };
  }, [family?.id]);

  async function saveRole() {
    if (!family || !memberId || reason.trim().length < 5) return;
    setBusy(true);
    try {
      await api.updateFamilyMemberRole({ familyId: family.id, memberId, role, reason: reason.trim() });
      setReason('');
      const nextMembers = await api.adminFamilyMembers(family.id);
      setMembers(nextMembers);
      await onUpdated();
    } catch (currentError) {
      onError(errorMessage(currentError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="twoColumns">
      <article className="panel">
        <h2>Familienrollen</h2>
        <p className="muted">Rollenänderungen erfordern eine Begründung und schreiben Audit-Events. Owner-Rollen werden nicht über dieses Tool umgehängt.</p>
        <label>Familie<select value={family?.id ?? ''} onChange={(event) => { setSelected(event.target.value); setMemberId(''); }}>{families.map((item) => <option value={item.id} key={item.id}>{item.name} · {item.ownerEmail}</option>)}</select></label>
        <label>Mitglied<select value={memberId} onChange={(event) => setMemberId(event.target.value)}>{members.filter((member) => member.role !== 'owner').map((member) => <option value={member.id} key={member.id}>{member.displayName} · {member.email ?? 'ohne Mail'} · {member.roleLabel ?? member.role}</option>)}</select></label>
        <label>Neue Rolle<select value={role} onChange={(event) => setRole(event.target.value)}><option value="parent">Elternteil</option><option value="caregiver">Babysitter / Betreuung</option><option value="viewer">Nur ansehen</option></select></label>
        <label>Reason<textarea value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Warum wird die Rolle geändert?" /></label>
        <button className="button" onClick={saveRole} disabled={busy || !family || !memberId || reason.trim().length < 5}>{busy ? 'Speichern…' : 'Rolle speichern'}</button>
      </article>
      <article className="panel">
        <h2>Mitglieder</h2>
        <div className="tableWrap">
          <table className="denseTable">
            <thead><tr><th>Name</th><th>E-Mail</th><th>Rolle</th><th>Rechte</th></tr></thead>
            <tbody>{members.map((member) => <tr key={member.id}><td><strong>{member.displayName}</strong><span>{member.id}</span></td><td>{member.email ?? '—'}</td><td><span className="rolePill">{member.roleLabel ?? member.role}</span></td><td>{member.roleDescription ?? '—'}</td></tr>)}</tbody>
          </table>
        </div>
      </article>
    </section>
  );
}

function LicensesView({ families, onUpdated, onError }: { families: FamilySummary[]; onUpdated: () => Promise<void>; onError: (message: string) => void }) {
  const [selected, setSelected] = useState('');
  const [status, setStatus] = useState('unlocked');
  const [trialEnd, setTrialEnd] = useState('');
  const [licenseExpiresAt, setLicenseExpiresAt] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const family = families.find((item) => item.id === selected) ?? families[0];

  useEffect(() => {
    if (!selected && families[0]) setSelected(families[0].id);
  }, [families, selected]);

  async function save() {
    if (!family) return;
    setBusy(true);
    try {
      await api.updateFamilyLicense({ familyId: family.id, licenseStatus: status, trialEnd, licenseExpiresAt, note });
      await onUpdated();
    } catch (currentError) {
      onError(errorMessage(currentError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="twoColumns twoColumns--license">
      <article className="panel">
        <h2>Lizenz ändern</h2>
        <label>Familie<select value={family?.id ?? ''} onChange={(event) => setSelected(event.target.value)}>{families.map((item) => <option value={item.id} key={item.id}>{item.name} · {item.ownerEmail}</option>)}</select></label>
        <label>Status<select value={status} onChange={(event) => setStatus(event.target.value)}><option value="trial">Trial</option><option value="unlocked">Aktiv / Unlocked</option><option value="expired">Abgelaufen</option><option value="demo">Demo</option><option value="disabled">Gesperrt</option></select></label>
        <label>Trial-Ende<input type="datetime-local" value={trialEnd} onChange={(event) => setTrialEnd(event.target.value)} /></label>
        <label>Lizenz-Ablauf<input type="datetime-local" value={licenseExpiresAt} onChange={(event) => setLicenseExpiresAt(event.target.value)} /></label>
        <label>Reason<textarea value={note} onChange={(event) => setNote(event.target.value)} placeholder="Pflicht: Warum wird die Lizenz geändert?" /></label>
        <button className="button" onClick={save} disabled={busy || !family || note.trim().length < 5}>{busy ? 'Speichern…' : 'Lizenz speichern'}</button>
      </article>
      <article className="panel">
        <h2>Aktuelle Lizenz</h2>
        {family ? (
          <dl className="detailList">
            <dt>Familie</dt><dd>{family.name}</dd>
            <dt>Owner</dt><dd>{family.ownerEmail || '—'}</dd>
            <dt>Status</dt><dd><Badge value={family.licenseStatus} /></dd>
            <dt>Trial-Ende</dt><dd>{formatDateTime(family.trialEndsAt)}</dd>
            <dt>Lizenz-Ablauf</dt><dd>{formatDateTime(family.licenseExpiresAt)}</dd>
            <dt>Letzte Änderung</dt><dd>{formatDateTime(family.licenseUpdatedAt)}</dd>
          </dl>
        ) : <p className="muted">Keine Familie geladen.</p>}
      </article>
    </section>
  );
}

type LogChip = 'payload' | 'error' | 'user' | 'family' | 'child' | 'request' | 'timeline';
type QuickLogRange = { label: string; days: number | null };

const quickLogRanges: QuickLogRange[] = [
  { label: '24h', days: 1 },
  { label: '7 Tage', days: 7 },
  { label: '30 Tage', days: 30 },
  { label: 'Alle', days: null },
];

function LoggingView({
  settings,
  logs,
  pagination,
  settingsApiMissing,
  logsApiMissing,
  filters,
  onSaved,
  onRefreshLogs,
  onArchiveLogs,
  onArchiveAllLogs,
  onDownload,
  onError,
}: {
  settings: LoggingSettings | null;
  logs: EnterpriseLogEntry[];
  pagination: EnterpriseLogPagination;
  settingsApiMissing: boolean;
  logsApiMissing: boolean;
  filters: EnterpriseLogQuery;
  onSaved: () => Promise<void>;
  onRefreshLogs: (filters: EnterpriseLogQuery) => Promise<void>;
  onArchiveLogs: (logIds: string[]) => Promise<void>;
  onArchiveAllLogs: (filters: EnterpriseLogQuery) => Promise<void>;
  onDownload: () => Promise<void>;
  onError: (message: string) => void;
}) {
  const [mode, setMode] = useState<LoggingSettings['mode']>(settings?.mode ?? 'standard');
  const [debugUntil, setDebugUntil] = useState(settings?.debugUntil ?? '');
  const [busy, setBusy] = useState(false);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [archiveAllBusy, setArchiveAllBusy] = useState(false);
  const [level, setLevel] = useState(filters.level ?? '');
  const [source, setSource] = useState(filters.source ?? '');
  const [eventName, setEventName] = useState(filters.eventName ?? '');
  const [search, setSearch] = useState(filters.search ?? '');
  const [since, setSince] = useState(isoToLocalDateTimeValue(filters.since));
  const [until, setUntil] = useState(isoToLocalDateTimeValue(filters.until));
  const [limit, setLimit] = useState(String(filters.limit ?? DEFAULT_LOG_FILTER_LIMIT));
  const [includeArchived, setIncludeArchived] = useState(Boolean(filters.includeArchived));
  const [openByLogId, setOpenByLogId] = useState<Record<string, LogChip | null>>({});
  const [selectedByLogId, setSelectedByLogId] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setMode(settings?.mode ?? 'standard');
    setDebugUntil(settings?.debugUntil ?? '');
  }, [settings?.mode, settings?.debugUntil]);

  useEffect(() => {
    setSelectedByLogId((current) => {
      const visibleIds = new Set(logs.map((entry) => entry.id));
      return Object.fromEntries(Object.entries(current).filter(([id]) => visibleIds.has(id)));
    });
  }, [logs]);

  async function save() {
    setBusy(true);
    try {
      await api.updatePlatformLoggingSettings({ mode, debugUntil: debugUntil || null });
      await onSaved();
    } catch (currentError) {
      onError(errorMessage(currentError));
    } finally {
      setBusy(false);
    }
  }

  async function download() {
    setDownloadBusy(true);
    try {
      await onDownload();
    } catch (currentError) {
      onError(errorMessage(currentError));
    } finally {
      setDownloadBusy(false);
    }
  }

  function buildFilters(offset = 0, overrides: Partial<{ since: string; until: string; limit: string; includeArchived: boolean }> = {}): EnterpriseLogQuery {
    const normalizedLimit = Math.min(Math.max(Number(overrides.limit ?? limit) || DEFAULT_LOG_FILTER_LIMIT, 10), 200);
    return {
      limit: normalizedLimit,
      offset,
      level: level || undefined,
      source: source || undefined,
      eventName: eventName || undefined,
      search: search || undefined,
      since: localDateTimeValueToIso(overrides.since ?? since),
      until: localDateTimeValueToIso(overrides.until ?? until),
      includeArchived: (overrides.includeArchived ?? includeArchived) || undefined,
    };
  }

  function toggleChip(logId: string, chip: LogChip) {
    setOpenByLogId((current) => ({ ...current, [logId]: current[logId] === chip ? null : chip }));
  }

  function runLogAction(entry: EnterpriseLogEntry, action: LogChip | 'copyRequest') {
    if (action === 'copyRequest') {
      const requestId = entry.requestId ?? entry.request_id ?? '';
      if (requestId) void navigator.clipboard.writeText(requestId);
      return;
    }
    toggleChip(entry.id, action);
  }

  function closeNativeSelect(event: ChangeEvent<HTMLSelectElement>) {
    event.currentTarget.value = '';
  }

  function toggleSelected(logId: string) {
    setSelectedByLogId((current) => ({ ...current, [logId]: !current[logId] }));
  }

  function toggleAllVisible() {
    const allSelected = logs.length > 0 && logs.every((entry) => selectedByLogId[entry.id]);
    setSelectedByLogId((current) => {
      const next = { ...current };
      for (const entry of logs) next[entry.id] = !allSelected;
      return next;
    });
  }

  async function archiveSelected(ids: string[]) {
    if (ids.length === 0) return;
    const confirmed = window.confirm(`${ids.length} Log-Eintrag${ids.length === 1 ? '' : 'e'} aus der Übersicht archivieren?`);
    if (!confirmed) return;
    setArchiveBusy(true);
    try {
      await onArchiveLogs(ids);
      setSelectedByLogId({});
    } catch (currentError) {
      onError(errorMessage(currentError));
    } finally {
      setArchiveBusy(false);
    }
  }

  async function archiveAllFiltered() {
    const currentFilters = buildFilters(0);
    const rangeLabel = since || until ? 'für die aktuelle Filteransicht' : 'aus der aktuellen Übersicht';
    const confirmed = window.confirm(`Alle nicht archivierten Logs ${rangeLabel} löschen? Sie verschwinden aus der Standardübersicht, bleiben aber über „Archivierte mit anzeigen“ nachvollziehbar.`);
    if (!confirmed) return;
    setArchiveAllBusy(true);
    try {
      await onArchiveAllLogs(currentFilters);
      setSelectedByLogId({});
    } catch (currentError) {
      onError(errorMessage(currentError));
    } finally {
      setArchiveAllBusy(false);
    }
  }

  async function applyQuickRange(days: number | null) {
    const nextSince = days === null ? '' : isoToLocalDateTimeValue(daysAgoIso(days));
    const nextUntil = '';
    setSince(nextSince);
    setUntil(nextUntil);
    await onRefreshLogs(buildFilters(0, { since: nextSince, until: nextUntil }));
  }

  const selectedLogIds = logs.filter((entry) => selectedByLogId[entry.id]).map((entry) => entry.id);
  const pageStart = pagination.total === 0 ? 0 : pagination.offset + 1;
  const pageEnd = Math.min(pagination.total, pagination.offset + pagination.limit);

  return (
    <section className="stack">
      <article className="panel">
        <h2>Enterprise Logging</h2>
        {settingsApiMissing ? <div className="alert alert--warn">Die vorhandene API hat die neue `/platform/logging/settings` Route noch nicht deployed. Die UI ist vorbereitet; die ganze API-Erweiterung liegt im Paket unter `api-extension/`.</div> : null}
        <div className="formGrid">
          <label>Logging-Modus<select value={mode} onChange={(event) => setMode(event.target.value as LoggingSettings['mode'])}><option value="off">Aus</option><option value="standard">Standard</option><option value="extended">Erweitert</option><option value="debug">Debug</option></select></label>
          <label>Debug bis<input type="datetime-local" value={debugUntil ?? ''} onChange={(event) => setDebugUntil(event.target.value)} /></label>
        </div>
        <div className="loggingLevels">
          <article><strong>Audit</strong><span>immer aktiv, nicht abschaltbar</span></article>
          <article><strong>Standard</strong><span>Fehler, Lizenzänderungen, Login-/Sync-Probleme</span></article>
          <article><strong>Extended</strong><span>zusätzlich Request-Kontext, Client-Versionen und Feature-Nutzung</span></article>
          <article><strong>Debug</strong><span>temporär begrenzt, keine Passwörter, Tokens oder sensiblen Kinderdetails</span></article>
        </div>
        <button className="button" disabled={busy || settingsApiMissing} onClick={save}>{busy ? 'Speichern…' : 'Logging speichern'}</button>
      </article>

      <article className="panel">
        <div className="panelHeader">
          <div>
            <h2>Support & Debug Cockpit</h2>
            <p className="muted">Aussagekräftige Fehleranalyse mit Archiv, Paging, Datums-Chips, Request-ID, Kontext-Chips und Familien-Timeline.</p>
          </div>
          <div className="toolbar toolbar--inline">
            <button className="button button--secondary" onClick={() => onRefreshLogs(buildFilters(0))}>Aktualisieren</button>
            <button className="button" onClick={download} disabled={downloadBusy || logsApiMissing}>{downloadBusy ? 'Export…' : 'CSV Download'}</button>
          </div>
        </div>

        <div className="quickFilterBar" aria-label="Schnelle Datumsfilter">
          {quickLogRanges.map((range) => <button key={range.label} className="quickFilterChip" onClick={() => applyQuickRange(range.days)}>{range.label}</button>)}
        </div>

        <div className="formGrid logFilterGrid">
          <label>Level<select value={level} onChange={(event) => setLevel(event.target.value)}><option value="">Alle</option><option value="fatal">fatal</option><option value="error">error</option><option value="warn">warn</option><option value="info">info</option><option value="debug">debug</option></select></label>
          <label>Quelle<input value={source} onChange={(event) => setSource(event.target.value)} placeholder="client.app / api.worker" /></label>
          <label>Event<input value={eventName} onChange={(event) => setEventName(event.target.value)} placeholder="http.request.failed" /></label>
          <label>Suche<input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Request-ID, User, Familie, Fehlercode" /></label>
          <label>Von<input type="datetime-local" value={since} onChange={(event) => setSince(event.target.value)} /></label>
          <label>Bis<input type="datetime-local" value={until} onChange={(event) => setUntil(event.target.value)} /></label>
          <label>Pro Seite<select value={limit} onChange={(event) => setLimit(event.target.value)}><option value="25">25</option><option value="50">50</option><option value="100">100</option><option value="200">200</option></select></label>
          <label className="checkboxLabel"><input type="checkbox" checked={includeArchived} onChange={(event) => setIncludeArchived(event.target.checked)} /> Archivierte mit anzeigen</label>
        </div>

        <div className="logBulkToolbar">
          <div className="toolbar toolbar--inline">
            <button className="button button--secondary" onClick={() => onRefreshLogs(buildFilters(0))}>Filter anwenden</button>
            <button className="button button--ghost" onClick={toggleAllVisible} disabled={logs.length === 0}>{logs.length > 0 && logs.every((entry) => selectedByLogId[entry.id]) ? 'Auswahl aufheben' : 'Sichtbare auswählen'}</button>
            <button className="button button--secondary" onClick={() => archiveSelected(selectedLogIds)} disabled={archiveBusy || selectedLogIds.length === 0 || logsApiMissing}>{archiveBusy ? 'Archiviere…' : `Auswahl löschen${selectedLogIds.length > 0 ? ` (${selectedLogIds.length})` : ''}`}</button>
            <button className="button button--danger" onClick={archiveAllFiltered} disabled={archiveAllBusy || logsApiMissing || pagination.total === 0}>{archiveAllBusy ? 'Lösche…' : 'Alle Logs löschen'}</button>
          </div>
          <p className="muted">{pageStart}–{pageEnd} von {pagination.total} Einträgen · Standardmäßig ohne archivierte Logs.</p>
        </div>

        {logsApiMissing ? <div className="alert alert--warn">Die `/platform/logs` Route ist noch nicht deployed. Nach API-Deployment erscheinen hier die Enterprise Logs.</div> : null}
        <div className="logTableWrap">
          <table className="logTable">
            <thead>
              <tr>
                <th className="logTable__select"><input type="checkbox" aria-label="Alle sichtbaren Logs auswählen" checked={logs.length > 0 && logs.every((entry) => selectedByLogId[entry.id])} onChange={toggleAllVisible} /></th>
                <th>Zeit</th>
                <th>Level</th>
                <th>Status</th>
                <th>Event / Meldung</th>
                <th>Route</th>
                <th>Kontext</th>
                <th>Request</th>
                <th className="logTable__actionsHead">Details</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((entry) => {
                const openChip = openByLogId[entry.id] ?? null;
                const archived = Boolean(entry.archivedAt ?? entry.archived_at);
                return (
                  <Fragment key={entry.id}>
                    <tr className={archived ? 'logTable__row logTable__row--archived' : 'logTable__row'}>
                      <td className="logTable__select"><input type="checkbox" aria-label="Log auswählen" checked={Boolean(selectedByLogId[entry.id])} onChange={() => toggleSelected(entry.id)} /></td>
                      <td className="logTable__time"><span>{formatDateTime(entry.createdAt ?? entry.created_at)}</span>{archived ? <small>archiviert</small> : null}</td>
                      <td><span className={`logLevel logLevel--${entry.level}`}>{entry.level}</span></td>
                      <td className="logTable__status"><strong>{logStatusLabel(entry)}</strong><small>{entry.code ?? '—'}</small></td>
                      <td className="logTable__event"><strong>{entry.eventName ?? entry.event_name ?? '—'}</strong><span>{logShortMessage(entry)}</span></td>
                      <td className="logTable__route"><strong>{entry.method ?? '—'}</strong><span>{entry.route ?? '—'}</span></td>
                      <td className="logTable__context">{logContextItems(entry).map((item) => <span key={item}>{item}</span>)}</td>
                      <td className="logTable__request"><span>{entry.requestId ?? entry.request_id ?? '—'}</span></td>
                      <td className="logTable__actions">
                        <LogChipButton label="Error Details" chip="error" active={openChip === 'error'} onClick={() => toggleChip(entry.id, 'error')} />
                        <select aria-label="Weitere Log-Details" defaultValue="" onChange={(event) => { const value = event.target.value as LogChip | 'copyRequest' | ''; if (value) runLogAction(entry, value); closeNativeSelect(event); }}>
                          <option value="">Mehr…</option>
                          <option value="payload">Payload</option>
                          <option value="user">User</option>
                          <option value="family">Familie</option>
                          <option value="child">Kind</option>
                          <option value="request">Request</option>
                          <option value="timeline">Timeline</option>
                          <option value="copyRequest">Request-ID kopieren</option>
                        </select>
                      </td>
                    </tr>
                    {openChip ? <tr className="logTable__detailRow"><td /><td colSpan={8}><LogChipPanel entry={entry} chip={openChip} /></td></tr> : null}
                  </Fragment>
                );
              })}
              {logs.length === 0 ? <tr><td colSpan={9} className="emptyCell">Keine Enterprise Logs für diese Filter gefunden.</td></tr> : null}
            </tbody>
          </table>
        </div>

        <div className="paginationBar">
          <button className="button button--secondary" disabled={!pagination.hasPrevious} onClick={() => onRefreshLogs(buildFilters(pagination.previousOffset ?? 0))}>Zurück</button>
          <span>{pageStart}–{pageEnd} von {pagination.total}</span>
          <button className="button button--secondary" disabled={!pagination.hasNext} onClick={() => onRefreshLogs(buildFilters(pagination.nextOffset ?? pagination.offset + pagination.limit))}>Weiter</button>
        </div>
      </article>
    </section>
  );
}

function LogChipButton({ label, active, onClick }: { label: string; chip: LogChip; active: boolean; onClick: () => void }) {
  return <button className={`logChip ${active ? 'logChip--active' : ''}`} onClick={onClick}>{label}</button>;
}

function LogChipPanel({ entry, chip }: { entry: EnterpriseLogEntry; chip: LogChip }) {
  switch (chip) {
    case 'payload': return <InlinePayloadCard payload={entry.metadata ?? entry.metadataJson ?? entry.metadata_json ?? entry.payloadJson ?? entry.payload_json} />;
    case 'error': return <InlineDetailsCard title="Error Details" rows={[
      ['Kurzmeldung', logShortMessage(entry)],
      ['User Message', entry.userMessage ?? entry.user_message ?? '—'],
      ['Technical Message', entry.technicalMessage ?? entry.technical_message ?? '—'],
      ['Code', entry.code ?? '—'],
      ['Status', String(entry.status ?? '—')],
      ['Route', entry.route ?? '—'],
      ['Methode', entry.method ?? '—'],
      ['Request-ID', entry.requestId ?? entry.request_id ?? '—'],
      ['Correlation-ID', entry.correlationId ?? entry.correlation_id ?? '—'],
      ['Session-ID', entry.sessionId ?? entry.session_id ?? entry.entityId ?? entry.entity_id ?? '—'],
      ['Dauer', `${entry.durationMs ?? entry.duration_ms ?? '—'} ms`],
    ]} payload={logErrorDetailsPayload(entry)} />;
    case 'user': return <InlineDetailsCard title="User" rows={[
      ['User-ID', entry.userId ?? entry.user_id ?? '—'],
      ['App Version', entry.appVersion ?? entry.app_version ?? '—'],
      ['Plattform', entry.platform ?? '—'],
      ['Device', entry.device ?? '—'],
    ]} actionLabel="Zum Benutzer" />;
    case 'family': return <InlineDetailsCard title="Familie" rows={[
      ['Family-ID', entry.familyId ?? entry.family_id ?? '—'],
      ['Event-ID', entry.eventId ?? entry.event_id ?? '—'],
      ['Entity', `${entry.entityType ?? entry.entity_type ?? '—'} / ${entry.entityId ?? entry.entity_id ?? '—'}`],
      ['Aktive Sessions', 'siehe Family Event Stream / Detailseite'],
    ]} actionLabel="Zur Familie" />;
    case 'child': return <InlineDetailsCard title="Kind" rows={[
      ['Child-ID', entry.childId ?? entry.child_id ?? '—'],
      ['Entity-ID', entry.entityId ?? entry.entity_id ?? '—'],
      ['Letzte Änderung', formatDateTime(entry.createdAt ?? entry.created_at)],
    ]} actionLabel="Zum Kind" />;
    case 'request': return <RequestCard entry={entry} />;
    case 'timeline': return <TimelineCard entry={entry} />;
    default: return null;
  }
}

function InlinePayloadCard({ payload }: { payload: unknown }) {
  const text = stringifyMetadata(payload);
  return <div className="logInlineCard"><div className="payloadToolbar"><strong>Payload</strong><button className="button button--secondary" onClick={() => navigator.clipboard.writeText(text)}>Kopieren</button></div><textarea readOnly value={text} onFocus={(event) => event.currentTarget.select()} /></div>;
}

function InlineDetailsCard({ title, rows, payload, actionLabel }: { title: string; rows: Array<[string, string]>; payload?: unknown; actionLabel?: string }) {
  return <div className="logInlineCard"><div className="logInlineCard__header"><strong>{title}</strong>{actionLabel ? <button className="button button--secondary">{actionLabel}</button> : null}</div><dl className="detailGrid">{rows.map(([key, value]) => <div key={key}><dt>{key}</dt><dd>{value}</dd></div>)}</dl>{payload ? <pre>{stringifyMetadata(payload)}</pre> : null}</div>;
}

function RequestCard({ entry }: { entry: EnterpriseLogEntry }) {
  const requestId = entry.requestId ?? entry.request_id ?? '—';
  return <div className="logInlineCard"><div className="logInlineCard__header"><strong>Request</strong><button className="button button--secondary" onClick={() => navigator.clipboard.writeText(requestId)}>Request-ID kopieren</button></div><dl className="detailGrid"><div><dt>Request-ID</dt><dd>{requestId}</dd></div><div><dt>Route</dt><dd>{entry.route ?? '—'}</dd></div><div><dt>Methode</dt><dd>{entry.method ?? '—'}</dd></div><div><dt>Status</dt><dd>{String(entry.status ?? '—')}</dd></div><div><dt>Dauer</dt><dd>{entry.durationMs ?? entry.duration_ms ?? '—'} ms</dd></div><div><dt>Worker</dt><dd>{entry.workerVersion ?? entry.worker_version ?? '—'}</dd></div><div><dt>API</dt><dd>{entry.apiVersion ?? entry.api_version ?? '—'}</dd></div><div><dt>App</dt><dd>{entry.appVersion ?? entry.app_version ?? '—'}</dd></div></dl></div>;
}

function TimelineCard({ entry }: { entry: EnterpriseLogEntry }) {
  const items = [
    ['Request empfangen', entry.createdAt ?? entry.created_at ?? ''],
    [entry.eventName ?? entry.event_name ?? 'Event verarbeitet', entry.route ?? entry.source],
    [entry.status && Number(entry.status) >= 400 ? 'Fehlerantwort erzeugt' : 'Antwort erzeugt', entry.code ?? String(entry.status ?? 'ok')],
    ['Analyse-Kontext', `User ${entry.userId ?? entry.user_id ?? '—'} · Familie ${entry.familyId ?? entry.family_id ?? '—'} · Kind ${entry.childId ?? entry.child_id ?? '—'}`],
    ['Sleep Session', `${entry.sessionId ?? entry.session_id ?? entry.entityId ?? entry.entity_id ?? '—'} · ${entry.code ?? entry.eventName ?? entry.event_name ?? 'ok'}`],
  ];
  return <div className="logInlineCard"><strong>Timeline</strong><ol className="timelineList">{items.map(([label, value]) => <li key={`${label}-${value}`}><span>{label}</span><small>{value}</small></li>)}</ol></div>;
}

function logShortMessage(entry: EnterpriseLogEntry): string {
  return entry.shortMessage ?? entry.short_message ?? entry.userMessage ?? entry.user_message ?? entry.technicalMessage ?? entry.technical_message ?? entry.eventName ?? entry.event_name ?? 'Logeintrag';
}

function logStatusLabel(entry: EnterpriseLogEntry): string {
  const status = entry.status;
  if (status === null || status === undefined || status === '') return '—';
  return String(status);
}

function compactId(label: string, value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  const cleanValue = value.trim();
  const compact = cleanValue.length > 16 ? `${cleanValue.slice(0, 8)}…${cleanValue.slice(-5)}` : cleanValue;
  return `${label}: ${compact}`;
}

function logContextItems(entry: EnterpriseLogEntry): string[] {
  const items = [
    compactId('User', entry.userId ?? entry.user_id),
    compactId('Fam', entry.familyId ?? entry.family_id),
    compactId('Kind', entry.childId ?? entry.child_id),
    compactId('Session', entry.sessionId ?? entry.session_id),
    compactId('Entity', entry.entityId ?? entry.entity_id),
  ].filter((item): item is string => Boolean(item));
  return items.length > 0 ? items : ['—'];
}

function logErrorDetailsPayload(entry: EnterpriseLogEntry): unknown {
  const explicit = entry.errorDetailsJson ?? entry.error_details_json;
  if (explicit) return explicit;
  return {
    code: entry.code ?? null,
    status: entry.status ?? null,
    shortMessage: logShortMessage(entry),
    userMessage: entry.userMessage ?? entry.user_message ?? null,
    technicalMessage: entry.technicalMessage ?? entry.technical_message ?? null,
    method: entry.method ?? null,
    route: entry.route ?? null,
    requestId: entry.requestId ?? entry.request_id ?? null,
    correlationId: entry.correlationId ?? entry.correlation_id ?? null,
    durationMs: entry.durationMs ?? entry.duration_ms ?? null,
    sessionId: entry.sessionId ?? entry.session_id ?? null,
    metadata: entry.metadata ?? entry.metadataJson ?? entry.metadata_json ?? null,
  };
}

function PayloadButton({ payload }: { payload: unknown }) {
  const [open, setOpen] = useState(false);
  return <div className="payloadBox"><button className="payloadButton" onClick={() => setOpen((current) => !current)}>payload</button>{open ? <InlinePayloadCard payload={payload} /> : null}</div>;
}

function AuditView({ entries }: { entries: AuditEntry[] }) {
  return (
    <section className="panel">
      <div className="tableWrap">
        <table className="denseTable">
          <thead><tr><th>Zeit</th><th>Aktion</th><th>Ziel</th><th>Actor</th><th>Metadata</th></tr></thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.id}>
                <td>{formatDateTime(entry.createdAt ?? entry.created_at)}</td>
                <td><strong>{entry.action}</strong></td>
                <td>{entry.targetType ?? entry.target_type ?? '—'}<span>{entry.targetId ?? entry.target_id ?? ''}</span></td>
                <td>{entry.actorUserId ?? entry.actor_user_id ?? '—'}</td>
                <td><PayloadButton payload={entry.metadata ?? entry.metadata_json} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SystemView({ health, rows, loggingApiMissing, onRefresh }: { health: HealthStatus | null; rows: AdminUserRow[]; loggingApiMissing: boolean; onRefresh: () => void }) {
  return (
    <section className="twoColumns">
      <article className="panel">
        <h2>API Health</h2>
        <dl className="detailList">
          <dt>Base URL</dt><dd>{api.baseUrl}</dd>
          <dt>Status</dt><dd>{health?.ok ? 'OK' : 'unbekannt'}</dd>
          <dt>Service</dt><dd>{String(health?.service ?? '—')}</dd>
          <dt>Datenbank</dt><dd>{String(health?.database ?? '—')}</dd>
          <dt>Timestamp</dt><dd>{formatDateTime(health?.timestamp as string | undefined)}</dd>
          <dt>Admin-Rows</dt><dd>{rows.length}</dd>
          <dt>Platform Logging API</dt><dd>{loggingApiMissing ? 'noch nicht deployed' : 'verfügbar'}</dd>
        </dl>
        <button className="button button--secondary" onClick={onRefresh}>Neu prüfen</button>
      </article>
      <article className="panel">
        <h2>Deployment Hinweise</h2>
        <p>Für Production muss `https://manage.siktra-sleep.com` in der API-CORS-Liste stehen.</p>
        <p>Cloudflare Pages Build Output ist `dist`.</p>
        <p>v0.6.11 erwartet zusätzlich `/app/version`, damit die PWA alte Builds zuverlässig erkennt und sich selbst aktualisiert.</p>
      </article>
    </section>
  );
}
